import classNames from 'classnames';
import { Heading } from 'components/1-atoms/Heading';
import { CatalogList } from 'components/2-molecules/CatalogList';
import { CatalogListItemProps } from 'components/2-molecules/CatalogListItem';
import { Spinner } from 'components/2-molecules/Spinner';
import { Pagination, PaginationTexts } from 'components/2-molecules/Table';
import { Container, ContainerWidth } from 'components/4-habitats/Container';
import { TopArea, TopAreaProps } from 'components/4-habitats/TopArea';
import { useState } from 'react';
import styles from './CatalogPage.module.scss';
import { getCurrentPageSizeAsNumber, getPaginatedListData } from './PaginationHelper';

export type CatalogPageProps = {
	additionalTop?: TopAreaProps['additional'];
	additionalPageContent?: React.ReactNode;
	catalogContent?: React.ReactNode[];
	catalogList: CatalogListItemProps[];
	ctaLink?: React.ReactNode;
	filters?: React.ReactNode;
	heading: string;
	loadingText: string;
	noItemsMessage?: string;
	pageSizeOptions: number[];
	pageSizeAllOption?: string;
	pageSizeOptionsDefaultValueIndex?: number;
	paginationErrorText: string;
	paginationTexts: PaginationTexts['paginationTexts'];
	paginationAriaLabels: PaginationTexts['paginationAriaLabels'];
	pageWidth?: ContainerWidth;
	searchPlaceholder?: string;
	teaser: string;
	topBanner?: React.ReactNode;
};

export const CatalogPage: React.FC<CatalogPageProps> = ({
	additionalTop,
	additionalPageContent,
	catalogContent,
	catalogList,
	ctaLink,
	filters,
	heading,
	loadingText,
	noItemsMessage,
	pageSizeOptions,
	pageSizeOptionsDefaultValueIndex,
	paginationErrorText,
	paginationTexts,
	paginationAriaLabels,
	pageWidth = 'Medium',
	teaser,
	topBanner,
}) => {
	const [currentPageSize, setCurrentPageSize] = useState<number | string>(pageSizeOptions[1]);
	const [currentPageNumber, setCurrentPageNumber] = useState<string>('1');
	const [currentPageNumberPageUpdate, setCurrentPageNumberPageUpdate] = useState<number>(1);
	const [currentPageNumberInputError, setCurrentPageNumberInputError] = useState<string>();

	const handleNewPage = (newPage) => {
		setCurrentPageNumber(newPage);
		setCurrentPageNumberPageUpdate(newPage);
		setCurrentPageNumberInputError(null);
		setTimeout(function () {
			window.scrollTo(0, 200);
		}, 2);
	};

	const handlePageChange = (e) => {
		if (e._reactName === 'onKeyDown' && e.key !== 'Enter') {
			return;
		}

		const currentPageSizeAsNumber = getCurrentPageSizeAsNumber(currentPageSize, catalogList);
		if (
			!Number.isNaN(Number(e.target.value)) &&
			Number(e.target.value) >= 1 &&
			Number(e.target.value) <= Math.ceil(catalogList.length / currentPageSizeAsNumber)
		) {
			setCurrentPageNumber(e.target.value);
			setCurrentPageNumberInputError(null);

			//update page when number is written
			if (e.target.value.toString() !== '') {
				if (e.key === 'Enter' || e._reactName === 'onBlur') {
					setCurrentPageNumberPageUpdate(e.target.value);
					setTimeout(function () {
						window.scrollTo(0, 200);
					}, 200);
				}
			}
		} else if (e.target.value === '') {
			setCurrentPageNumber(e.target.value);
			setCurrentPageNumberInputError(paginationErrorText);
		} else {
			setCurrentPageNumberInputError(paginationErrorText);
		}
	};

	const handlePageSizeChange = (e) => {
		setCurrentPageSize(Number(e.target.value));
	};

	return (
		<div className={styles.CatalogPage}>
			<TopArea additional={{ top: additionalTop.top }} topBanner={topBanner} />
			<Container width={pageWidth} padded>
				<div
					className={classNames(
						styles.CatalogPage_wrapper,
						{ [styles.CatalogPage_wrapper___medium]: pageWidth === 'Medium' },
						{ [styles.CatalogPage_wrapper___large]: pageWidth === 'Large' },
						{ [styles.CatalogPage_wrapper___xlarge]: pageWidth === 'X-Large' },
					)}
				>
					{additionalPageContent}
					{additionalTop?.bottom && additionalTop.bottom}
				</div>
				<div className={classNames(styles.CatalogPage_content, additionalPageContent && styles.CatalogPage_content___noTopMargin)}>
					<div className={styles.CatalogPage_contentHeader}>
						<Heading className={styles.CatalogPage_heading} headingLevel="h1" style="xl">
							{heading}
						</Heading>
						{ctaLink && <div className={styles.CatalogPage_cta}>{ctaLink}</div>}
					</div>
					{teaser && (
						<div className={styles.CatalogPage_teaser}>
							<p>{teaser}</p>
						</div>
					)}
					{filters}
					{catalogList ? (
						<>
							<CatalogList
								items={getPaginatedListData(
									catalogList,
									currentPageNumberPageUpdate,
									getCurrentPageSizeAsNumber(currentPageSize, catalogList),
								)?.map((item) => ({
									...item,
								}))}
								noItemsMessage={noItemsMessage}
							/>
							{catalogList.length > 0 && (
								<Pagination
									className={styles.CatalogPage_pagination}
									numberOfItemsTotal={catalogList.length}
									numberOfPages={Math.ceil(catalogList.length / getCurrentPageSizeAsNumber(currentPageSize, catalogList))}
									pageSizeOptions={pageSizeOptions}
									defaultValueIndex={pageSizeOptionsDefaultValueIndex}
									currentPageNumber={currentPageNumber}
									errorText={currentPageNumberInputError}
									paginationTexts={paginationTexts}
									paginationAriaLabels={paginationAriaLabels}
									onNextPageBtnClick={() => handleNewPage(Number(currentPageNumber) + 1)}
									onPrevPageBtnClick={() => handleNewPage(Number(currentPageNumber) - 1)}
									onPageSizeChange={handlePageSizeChange}
									onCurrentPageNumberChange={(e) => handlePageChange(e)}
									onCurrentPageKeyDown={(e) => handlePageChange(e)}
									onCurrentPageBlur={(e) => handlePageChange(e)}
								/>
							)}
						</>
					) : (
						<Spinner text={loadingText} />
					)}
				</div>

				{catalogContent}
			</Container>
		</div>
	);
};
